import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useOutletContext, useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { ErpEmpty, ErpLoading, ErpMetricCard, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import { ACCOUNT_TYPE_LABELS, ERP_BRAND, formatDateTime, formatMoney } from "../../../utils/erp";

export default function ProjectErpLedgerPage() {
  const { projectId } = useParams();
  const { bootstrap } = useOutletContext();
  const [entries, setEntries] = React.useState([]);
  const [balances, setBalances] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [filters, setFilters] = React.useState({ accountCode: "", dateFrom: "", dateTo: "", journalNumber: "" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = { ...filters };
      const [entryData, balanceData] = await Promise.all([
        erpApi.listLedger(projectId, { page: page + 1, pageSize: rowsPerPage, ...params }),
        erpApi.ledgerBalances(projectId, params),
      ]);
      setEntries(entryData.entries || []);
      setTotal(Number(entryData.total || 0));
      setBalances(balanceData.balances || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar el libro diario");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  React.useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const totals = React.useMemo(
    () => entries.reduce((result, entry) => {
      if (entry.side === "debit") result.debits += Number(entry.amount || 0);
      if (entry.side === "credit") result.credits += Number(entry.amount || 0);
      return result;
    }, { debits: 0, credits: 0 }),
    [entries],
  );
  const pageBalanced = Math.abs(totals.debits - totals.credits) < 0.01;

  return (
    <Stack spacing={2.4}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <ErpSectionTitle
        title="Libro diario"
        description="Asientos de doble partida generados por las operaciones confirmadas."
        action={<Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>Actualizar</Button>}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ErpMetricCard icon={<AccountBalanceRoundedIcon />} label="DEBE EN ESTA PÁGINA" value={formatMoney(totals.debits)} detail={`${entries.filter((item) => item.side === "debit").length} líneas`} tone="blue" />
        </Grid>
        <Grid item xs={12} md={4}>
          <ErpMetricCard icon={<AccountBalanceRoundedIcon />} label="HABER EN ESTA PÁGINA" value={formatMoney(totals.credits)} detail={`${entries.filter((item) => item.side === "credit").length} líneas`} tone="orange" />
        </Grid>
        <Grid item xs={12} md={4}>
          <ErpMetricCard icon={<BalanceRoundedIcon />} label="CONTROL DE BALANCE" value={pageBalanced ? "Balanceado" : "Con diferencia"} detail={formatMoney(totals.debits - totals.credits)} tone={pageBalanced ? "green" : "red"} />
        </Grid>
      </Grid>

      <ErpPanel>
        <Box sx={{ p: 2.2, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.3}>
            <TextField select size="small" label="Cuenta" value={filters.accountCode} onChange={(event) => setFilter("accountCode", event.target.value)} sx={{ minWidth: 260 }}>
              <MenuItem value="">Todas las cuentas</MenuItem>
              {(bootstrap.accounts || []).map((account) => <MenuItem key={account.code} value={account.code}>{account.code} · {account.name}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Número de asiento" value={filters.journalNumber} onChange={(event) => setFilter("journalNumber", event.target.value)} />
            <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
        </Box>
        {loading ? <ErpLoading label="Cargando asientos..." /> : !entries.length ? <ErpEmpty title="Sin asientos para mostrar" description="Confirmá una venta, compra u operación de caja para generar el primer asiento." /> : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Asiento / fecha</TableCell>
                    <TableCell>Cuenta</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Debe</TableCell>
                    <TableCell align="right">Haber</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id || entry._id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 900 }}>{entry.journalNumber}</Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatDateTime(entry.postedAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 850 }}>{entry.accountCode}</Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{entry.accountName}</Typography>
                      </TableCell>
                      <TableCell>{entry.description || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: entry.side === "debit" ? 900 : 400 }}>{entry.side === "debit" ? formatMoney(entry.amount, entry.currency) : "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: entry.side === "credit" ? 900 : 400 }}>{entry.side === "credit" ? formatMoney(entry.amount, entry.currency) : "—"}</TableCell>
                      <TableCell>{entry.createdByName || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} onPageChange={(_event, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página" />
          </>
        )}
      </ErpPanel>

      <ErpPanel>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Saldos por cuenta</Typography>
          <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35 }}>Saldo calculado según la naturaleza de cada cuenta.</Typography>
        </Box>
        {!balances.length ? <ErpEmpty title="Sin saldos" description="Los saldos aparecerán cuando existan asientos en el período seleccionado." /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cuenta</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Debe</TableCell>
                  <TableCell align="right">Haber</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell align="right">Movimientos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.map((balance) => (
                  <TableRow key={balance.accountCode} hover>
                    <TableCell><strong>{balance.accountCode}</strong> · {balance.accountName}</TableCell>
                    <TableCell><Chip size="small" label={ACCOUNT_TYPE_LABELS[balance.accountType] || balance.accountType || "—"} /></TableCell>
                    <TableCell align="right">{formatMoney(balance.debits)}</TableCell>
                    <TableCell align="right">{formatMoney(balance.credits)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950 }}>{formatMoney(balance.balance)}</TableCell>
                    <TableCell align="right">{balance.movements || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ErpPanel>
    </Stack>
  );
}
